import React from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import merchantImg from '../../../../assets/images/merchant-last-fold.png';
import css from './MerchantCta.module.css';

/**
 * MerchantCta — "Your next customer is one offer away" section.
 * Visual reference: lovable_template/src/components/site/Merchant.tsx
 *
 *   <section bg-background py-24>
 *     <div max-w-7xl grid lg:grid-cols-2 gap-12 lg:gap-16>
 *       <div>                                    ← image side
 *         <rounded-3xl img />
 *         <floating-card-1 (This week +128%) />
 *         <floating-card-2 (Today, 23 slots booked) />
 *       </div>
 *       <div>                                    ← content side
 *         <pill>For Business</pill>
 *         <h2>Your next customer is <hl>one offer away</hl></h2>
 *         <p>{subheading}</p>
 *         <ul>{benefits}</ul>
 *         <buttons>List your business · Learn more</buttons>
 *       </div>
 *     </div>
 *   </section>
 */

// Inline Lucide-style SVGs (matches HowItWorks pattern, no new dep).
const TrendingUpIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

const UsersIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CalendarIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect width="18" height="18" x="3" y="4" rx="2" />
    <path d="M3 10h18" />
  </svg>
);

const ArrowRightIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const BENEFIT_ICONS = {
  revenue: TrendingUpIcon,
  customers: UsersIcon,
  control: CalendarIcon,
};

const BENEFITS = [
  {
    key: 'revenue',
    titleId: 'MerchantCta.benefitRevenueTitle',
    descriptionId: 'MerchantCta.benefitRevenueDescription',
  },
  {
    key: 'customers',
    titleId: 'MerchantCta.benefitCustomersTitle',
    descriptionId: 'MerchantCta.benefitCustomersDescription',
  },
  {
    key: 'control',
    titleId: 'MerchantCta.benefitControlTitle',
    descriptionId: 'MerchantCta.benefitControlDescription',
  },
];

const MerchantCta = () => {
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const goToMerchantSignup = () => {
    history.push(
      createResourceLocatorString(
        'SignupForUserTypePage',
        routeConfiguration,
        { userType: 'provider' },
        {}
      )
    );
  };

  const goToMerchantLanding = () => {
    history.push(
      createResourceLocatorString('LandingPageMerchant', routeConfiguration, {}, {})
    );
  };

  // Heading parts so the yellow highlight wraps only "one offer away".
  const headingLead = intl.formatMessage({ id: 'MerchantCta.headingLead' });
  const headingHighlight = intl.formatMessage({ id: 'MerchantCta.headingHighlight' });

  return (
    <section
      className={classNames(css.merchantCtaWrapper, arabicLanguage && css.arabicDirection)}
    >
      <div className={css.inner}>
        {/* ----- Left: image + floating stat cards (hidden < sm) ----- */}
        <div className={css.mediaCol}>
          <div className={css.imageFrame}>
            <img
              src={merchantImg}
              alt={intl.formatMessage({ id: 'MerchantCta.imageAlt' })}
              loading="lazy"
              className={css.merchantImage}
            />
          </div>

          {/* Top-right card: "This week / +128% / ↑ Bookings".
              Lovable: `absolute -right-4 top-8 hidden ... sm:block`. */}
          <div className={classNames(css.statCard, css.statCardTop)}>
            <div className={css.statLabel}>
              {intl.formatMessage({ id: 'MerchantCta.statWeekLabel' })}
            </div>
            <div className={css.statValue}>+128%</div>
            <div className={css.statTrend}>
              <span className={css.statTrendArrow} aria-hidden="true">
                ↑
              </span>
              {intl.formatMessage({ id: 'MerchantCta.statBookings' })}
            </div>
          </div>

          {/* Bottom-left card: Calendar icon + "Today / 23 slots booked".
              Lovable: `absolute -left-4 bottom-8 hidden ... sm:block`. */}
          <div className={classNames(css.statCard, css.statCardBottom)}>
            <div className={css.statCardRow}>
              <span className={css.statIcon} aria-hidden="true">
                <CalendarIcon className={css.statIconSvg} />
              </span>
              <div>
                <div className={css.statLabel}>
                  {intl.formatMessage({ id: 'MerchantCta.statTodayLabel' })}
                </div>
                <div className={css.statValueSmall}>
                  {intl.formatMessage({ id: 'MerchantCta.statSlotsBooked' })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ----- Right: content ----- */}
        <div className={css.contentCol}>
          <div className={css.forBusinessPill}>
            {intl.formatMessage({ id: 'MerchantCta.forBusiness' })}
          </div>

          <h2 className={css.heading}>
            {headingLead}{' '}
            <span className={css.headingHighlight}>{headingHighlight}</span>
          </h2>

          <p className={css.subheading}>
            {intl.formatMessage({ id: 'MerchantCta.subheading' })}
          </p>

          <ul className={css.benefitsList}>
            {BENEFITS.map(b => {
              const Icon = BENEFIT_ICONS[b.key];
              return (
                <li key={b.key} className={css.benefitItem}>
                  <span className={css.benefitIcon} aria-hidden="true">
                    <Icon className={css.benefitIconSvg} />
                  </span>
                  <div className={css.benefitText}>
                    <div className={css.benefitTitle}>
                      {intl.formatMessage({ id: b.titleId })}
                    </div>
                    <div className={css.benefitDescription}>
                      {intl.formatMessage({ id: b.descriptionId })}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className={css.ctaRow}>
            <button
              type="button"
              className={css.primaryCta}
              onClick={goToMerchantSignup}
            >
              {intl.formatMessage({ id: 'MerchantCta.listYourBusiness' })}
              <ArrowRightIcon className={css.primaryCtaArrow} />
            </button>
            <button
              type="button"
              className={css.secondaryCta}
              onClick={goToMerchantLanding}
            >
              {intl.formatMessage({ id: 'MerchantCta.learnMore' })}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MerchantCta;
